import React, { useState, useRef, useCallback } from 'react';
import { CreateTransactionDTO, ImportMapping, ParsedCSVRow } from '../../types/budget';
import { useBudget } from './BudgetContext';
import { parseCSV, parseDate, parseAmount } from './importTransactionsUtils';
import { Button } from '../ui/Button';
import styles from './ImportTransactionsModal.module.css';

interface ImportTransactionsModalProps {
  onClose: () => void;
}

type ModalState = 'upload' | 'mapping' | 'preview' | 'importing' | 'success' | 'error';

export const ImportTransactionsModal: React.FC<ImportTransactionsModalProps> = ({
  onClose,
}) => {
  const { importTransactions, activeAccounts } = useBudget();
  const [state, setState] = useState<ModalState>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<ParsedCSVRow[]>([]);
  const [mapping, setMapping] = useState<ImportMapping>({
    dateColumn: null,
    descriptionColumn: null,
    amountColumn: null,
    categoryColumn: null,
  });
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [invertAmounts, setInvertAmounts] = useState(false);
  const [parsedTransactions, setParsedTransactions] = useState<CreateTransactionDTO[]>([]);
  const [importedCount, setImportedCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.csv') || droppedFile.type === 'text/csv')) {
      handleFileSelect(droppedFile);
    } else {
      setError('Please drop a CSV file');
    }
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setError(null);

    try {
      const text = await selectedFile.text();
      const { headers: parsedHeaders, rows: parsedRows } = parseCSV(text);

      if (parsedHeaders.length === 0) {
        setError('CSV file appears to be empty');
        return;
      }

      setHeaders(parsedHeaders);
      setRows(parsedRows);

      // Auto-detect common column names
      const dateCol = parsedHeaders.find(h =>
        /date|posted|trans.*date/i.test(h)
      );
      const descCol = parsedHeaders.find(h =>
        /description|payee|merchant|memo|name/i.test(h)
      );
      const amountCol = parsedHeaders.find(h =>
        /amount|total|sum|value/i.test(h)
      );

      setMapping({
        dateColumn: dateCol || null,
        descriptionColumn: descCol || null,
        amountColumn: amountCol || null,
        categoryColumn: null,
      });

      setState('mapping');
    } catch (err) {
      setError('Failed to parse CSV file');
      setState('error');
    }
  };

  const handleMappingChange = (field: keyof ImportMapping, value: string | null) => {
    setMapping(prev => ({ ...prev, [field]: value || null }));
  };

  const handlePreview = () => {
    if (!mapping.dateColumn || !mapping.descriptionColumn || !mapping.amountColumn) {
      setError('Please map Date, Description, and Amount columns');
      return;
    }
    
    // Account is required for import
    if (!selectedAccountId) {
      setError('Please select an account for the imported transactions');
      return;
    }

    const transactions: CreateTransactionDTO[] = [];
    const errors: string[] = [];

    rows.forEach((row, index) => {
      const dateStr = row[mapping.dateColumn!];
      const description = row[mapping.descriptionColumn!];
      const amountStr = row[mapping.amountColumn!];

      const date = parseDate(dateStr);
      const amount = parseAmount(amountStr, invertAmounts);

      if (!date) {
        errors.push(`Row ${index + 2}: Invalid date "${dateStr}"`);
        return;
      }

      if (amount === null) {
        errors.push(`Row ${index + 2}: Invalid amount "${amountStr}"`);
        return;
      }

      if (!description || !description.trim()) {
        errors.push(`Row ${index + 2}: Missing description`);
        return;
      }

      transactions.push({
        date,
        description: description.trim(),
        originalDescription: description.trim(),
        amount,
        categoryId: null,
        accountId: selectedAccountId!, // Account is validated as required before this point
      });
    });

    if (transactions.length === 0) {
      setError(`No valid transactions found. ${errors.slice(0, 3).join('. ')}`);
      return;
    }

    setParsedTransactions(transactions);
    setError(null);
    setState('preview');
  };

  const handleImport = async () => {
    setState('importing');
    setError(null);

    try {
      await importTransactions(parsedTransactions, selectedAccountId);
      setImportedCount(parsedTransactions.length);
      setState('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import transactions');
      setState('error');
    }
  };

  const handleRetry = () => {
    setFile(null);
    setHeaders([]);
    setRows([]);
    setParsedTransactions([]);
    setSelectedAccountId(null);
    setError(null);
    setState('upload');
  };

  const renderUploadState = () => (
    <>
      <h2>Import Transactions</h2>
      <p className={styles.instructions}>
        Upload a CSV file from your bank or credit card statement.
      </p>

      <div
        className={`${styles.dropZone} ${isDragging ? styles.dragging : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className={styles.dropZoneContent}>
          <span className={styles.dropZoneIcon}>📄</span>
          <span className={styles.dropZoneText}>
            Drop CSV file here or click to browse
          </span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileInputChange}
          className={styles.fileInput}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.buttons}>
        <Button variant="secondary" size="md" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </>
  );

  const renderMappingState = () => (
    <>
      <h2>Map Columns</h2>
      <p className={styles.instructions}>
        Match the CSV columns to transaction fields.
      </p>

      {/* Preview table */}
      <div className={styles.previewTable}>
        <table>
          <thead>
            <tr>
              {headers.map((header) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 3).map((row, i) => (
              <tr key={i}>
                {headers.map((header) => (
                  <td key={header}>{row[header]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Column mapping */}
      <div className={styles.mappingForm}>
        <div className={styles.mappingField}>
          <label>Date Column *</label>
          <select
            value={mapping.dateColumn || ''}
            onChange={(e) => handleMappingChange('dateColumn', e.target.value)}
          >
            <option value="">Select column...</option>
            {headers.map((h) => (
              <option key={h} value={h}>{h}</option>
            ))}
          </select>
        </div>

        <div className={styles.mappingField}>
          <label>Description Column *</label>
          <select
            value={mapping.descriptionColumn || ''}
            onChange={(e) => handleMappingChange('descriptionColumn', e.target.value)}
          >
            <option value="">Select column...</option>
            {headers.map((h) => (
              <option key={h} value={h}>{h}</option>
            ))}
          </select>
        </div>

        <div className={styles.mappingField}>
          <label>Amount Column *</label>
          <select
            value={mapping.amountColumn || ''}
            onChange={(e) => handleMappingChange('amountColumn', e.target.value)}
          >
            <option value="">Select column...</option>
            {headers.map((h) => (
              <option key={h} value={h}>{h}</option>
            ))}
          </select>
        </div>

        <div className={styles.mappingField}>
          <label>Account *</label>
          <select
            value={selectedAccountId || ''}
            onChange={(e) => setSelectedAccountId(e.target.value || null)}
            required
          >
            <option value="">Select Account *</option>
            {activeAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
                {account.lastFour ? ` (****${account.lastFour})` : ''}
              </option>
            ))}
          </select>
        </div>

        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={invertAmounts}
            onChange={(e) => setInvertAmounts(e.target.checked)}
          />
          <span>Invert amounts (for liability accounts)</span>
        </label>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.buttons}>
        <Button variant="secondary" size="md" onClick={handleRetry}>
          Back
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={handlePreview}
          disabled={!mapping.dateColumn || !mapping.descriptionColumn || !mapping.amountColumn || !selectedAccountId}
        >
          Preview
        </Button>
      </div>
    </>
  );

  const renderPreviewState = () => (
    <>
      <h2>Preview Import</h2>

      <div className={styles.previewSummary}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryIcon}>📁</span>
          <span className={styles.summaryLabel}>File:</span>
          <span className={styles.summaryValue}>{file?.name}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryIcon}>📊</span>
          <span className={styles.summaryLabel}>Transactions to import:</span>
          <span className={styles.summaryValue}>{parsedTransactions.length}</span>
        </div>
        {parsedTransactions.length !== rows.length && (
          <div className={`${styles.summaryItem} ${styles.warningItem}`}>
            <span className={styles.summaryIcon}>⚠️</span>
            <span className={styles.summaryLabel}>Skipped rows:</span>
            <span className={styles.summaryValue}>{rows.length - parsedTransactions.length}</span>
          </div>
        )}
      </div>

      {/* Sample transactions */}
      <div className={styles.sampleTransactions}>
        <h3>Sample Transactions</h3>
        {parsedTransactions.slice(0, 5).map((t, i) => (
          <div key={i} className={styles.sampleRow}>
            <span className={styles.sampleDate}>{t.date}</span>
            <span className={styles.sampleDescription}>{t.description}</span>
            <span className={`${styles.sampleAmount} ${t.amount < 0 ? styles.expense : styles.income}`}>
              {t.amount < 0 ? '-' : '+'}${Math.abs(t.amount).toFixed(2)}
            </span>
          </div>
        ))}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.buttons}>
        <Button variant="secondary" size="md" onClick={() => setState('mapping')}>
          Back
        </Button>
        <Button variant="primary" size="md" onClick={handleImport}>
          Import {parsedTransactions.length} Transactions
        </Button>
      </div>
    </>
  );

  const renderImportingState = () => (
    <>
      <h2>Importing Transactions</h2>
      <div className={styles.loadingContainer}>
        <div className={styles.spinner} />
        <p>Please wait while your transactions are being imported...</p>
      </div>
    </>
  );

  const renderSuccessState = () => (
    <>
      <h2>Import Complete</h2>

      <div className={styles.resultSummary}>
        <div className={styles.successIcon}>✅</div>
        <p className={styles.successMessage}>
          Successfully imported {importedCount} transaction{importedCount !== 1 ? 's' : ''}
        </p>
        <p className={styles.successHint}>
          Transactions are marked as "Uncategorized" and ready for categorization.
        </p>
      </div>

      <div className={styles.buttons}>
        <Button variant="primary" size="md" onClick={onClose}>
          Done
        </Button>
      </div>
    </>
  );

  const renderErrorState = () => (
    <>
      <h2>Import Failed</h2>

      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>❌</div>
        <p className={styles.errorMessage}>{error || 'An unknown error occurred'}</p>
      </div>

      <div className={styles.buttons}>
        <Button variant="secondary" size="md" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="md" onClick={handleRetry}>
          Try Again
        </Button>
      </div>
    </>
  );

  const renderContent = () => {
    switch (state) {
      case 'upload':
        return renderUploadState();
      case 'mapping':
        return renderMappingState();
      case 'preview':
        return renderPreviewState();
      case 'importing':
        return renderImportingState();
      case 'success':
        return renderSuccessState();
      case 'error':
        return renderErrorState();
      default:
        return null;
    }
  };

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        {renderContent()}
      </div>
    </div>
  );
};

export default ImportTransactionsModal;
